import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Gauge, Users, Clock, BarChart3, ListChecks, ArrowRight } from 'lucide-react';
import type { DeptHeadHomeData } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';
import { Card, Hero, MonoLabel, Bar, StageBand, Avatar, ApprovalQueue } from './ui';

// Manager / department-head home. Matches the design's `manager_home`: a black
// "guided hero" (greeting + team stage + a nudge to the pending approvals) over
// the stat row, journey bars, and the private people list + approval queue.
// Honest-data rule: the hero's stage/counts come from the payload — no invented
// trend arrows. The one derivable badge (participation %) is computed, not faked.

const greetingFor = (hour: number) =>
  hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

const DeptHeadHome: React.FC<{ data: DeptHeadHomeData; onRefresh: () => void }> = ({ data, onRefresh }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const s = data.stats;
  const pending = data.approvals.length;
  const firstName = (localStorage.getItem('user_name') || localStorage.getItem('user_email')?.split('@')[0] || '')
    .trim().split(/\s+/)[0];
  const participationPct = s.total > 0 ? Math.round((s.onboarded / s.total) * 100) : null;

  const decide = async (id: string, decision: string, comment?: string) => {
    setBusy(id);
    try { await aaiApi.decideRequest(id, { decision, comment }); window.dispatchEvent(new Event('aai:approvals-changed')); onRefresh(); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      {/* guided hero — greeting + where the team stands + what needs the manager */}
      <Hero className="relative overflow-hidden !p-6">
        <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-brand-500/20 blur-2xl pointer-events-none" />
        <div className="relative">
          <MonoLabel tone="accent" className="mb-2">{data.department ? `${data.department} · your team` : 'Your team'}</MonoLabel>
          <h1 className="text-[23px] font-bold tracking-[-0.01em] leading-tight max-w-xl">
            {greetingFor(new Date().getHours())}{firstName ? `, ${firstName}` : ''}. Your team is{' '}
            <span className="text-emerald-400 capitalize">{s.dept_stage || 'getting started'}</span>
            {pending > 0 && (
              <> — and {pending} request{pending === 1 ? '' : 's'} need{pending === 1 ? 's' : ''} you.</>
            )}
            {pending === 0 && '.'}
          </h1>
          <p className="text-[13px] text-white/60 mt-1.5 max-w-xl leading-relaxed">
            You're the bridge between your team and leadership. You see how the team is progressing and approve
            what they want built — never anyone's private numbers.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-4">
            <button onClick={() => navigate('/team-rollup')}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-white text-black rounded-full px-4 py-2 whitespace-nowrap">
              <BarChart3 size={15} /> See team rollup
            </button>
            {pending > 0 && (
              <button onClick={() => navigate('/requests')}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-white/10 hover:bg-white/15 text-white border border-white/20 rounded-full px-4 py-2 transition-colors whitespace-nowrap">
                <ListChecks size={15} /> Review {pending} request{pending === 1 ? '' : 's'}
              </button>
            )}
          </div>
        </div>
      </Hero>

      {/* stat row — icon + derived badge + band/count */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <Card>
          <div className="flex items-center justify-between mb-2.5">
            <span className="w-[34px] h-[34px] rounded-[9px] bg-brand-100 text-brand-600 grid place-items-center"><Gauge size={17} /></span>
          </div>
          <div className="text-[22px] font-bold capitalize">{s.dept_stage || '—'}</div>
          <div className="text-xs text-gray-500 mt-0.5">Dept readiness · band, not score</div>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-2.5">
            <span className="w-[34px] h-[34px] rounded-[9px] bg-brand-100 text-brand-600 grid place-items-center"><Users size={17} /></span>
            {participationPct !== null && <span className="text-[11px] font-semibold text-gray-500">{participationPct}%</span>}
          </div>
          <div className="text-[22px] font-bold">{s.onboarded} <span className="text-sm font-medium text-gray-400">/ {s.total}</span></div>
          <div className="text-xs text-gray-500 mt-0.5">Onboarded &amp; active</div>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-2.5">
            <span className="w-[34px] h-[34px] rounded-[9px] bg-accent-100 text-amber-700 grid place-items-center"><Clock size={17} /></span>
            <span className="text-[11px] font-semibold text-gray-500">range</span>
          </div>
          <div className="text-[22px] font-bold">{s.time_redirected.low}–{s.time_redirected.high}
            <span className="text-[13px] font-medium text-gray-500"> {s.time_redirected.unit}</span></div>
          <div className="text-xs text-gray-500 mt-0.5">Time redirected · team aggregate</div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-3.5">
          <MonoLabel>{data.department || 'Department'} · Educate → Empower → Equip</MonoLabel>
          <button onClick={() => navigate('/team-rollup')}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:text-brand-700">
            Full breakdown <ArrowRight size={13} />
          </button>
        </div>
        {data.journey_bars.map((b) => (
          <div key={b.label} className="mb-3 last:mb-0">
            <div className="flex justify-between text-[12.5px] mb-1.5"><span className="font-semibold">{b.label}</span><span className="text-gray-500">{b.val}</span></div>
            <Bar pct={b.pct} color="brand" height="h-2" />
          </div>
        ))}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* your people — stage only, never numbers */}
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-bold text-sm">Your people</span>
            <span className="text-[11px] text-gray-400 flex items-center gap-1"><Lock size={12} /> Numbers private</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">You see who's enrolled and their stage — not their individual usage or coaching notes.</p>
          {data.people.map((p) => (
            <div key={p.name} className="flex items-center gap-2.5 py-2 border-t border-gray-50 first:border-0">
              <Avatar initials={p.initials} />
              <span className="text-[13px] font-medium flex-1 truncate">{p.name}</span>
              {p.onboarded ? <StageBand stage={p.stage} /> : <span className="text-[11px] text-gray-400">Pending</span>}
              <Lock size={13} className="text-gray-300" />
            </div>
          ))}
          {data.people.length === 0 && <p className="text-[13px] text-gray-400">No one enrolled in this department yet.</p>}
        </div>

        {/* approval queue — gate 1; Approve forwards the request to your AI champion */}
        <ApprovalQueue items={data.approvals} onDecide={decide} busyId={busy} title="Approval queue" />
      </div>
    </div>
  );
};

export default DeptHeadHome;
